import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchChatMessageReadStatusDetails } from './chatMessageReadStatusApi';

const normalizeMessageId = (value) => String(value || '').trim();

const resolveRoomKey = ({ scope, eventId, threadId, enabled }) => {
  if (!enabled) return '';
  if (scope === 'group') return threadId ? `group:${threadId}` : '';
  return eventId ? `event:${eventId}` : '';
};

const useChatMessageReadStatus = ({
  scope = 'event',
  eventId = '',
  threadId = '',
  enabled = true,
  onError = null,
} = {}) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMessageId, setDetailMessageId] = useState('');
  const [detailReaders, setDetailReaders] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const roomKey = useMemo(
    () => resolveRoomKey({ scope, eventId, threadId, enabled }),
    [enabled, eventId, scope, threadId],
  );

  const requestParams = useMemo(
    () => ({ scope, eventId, threadId }),
    [eventId, scope, threadId],
  );

  const reportError = useCallback((error, fallbackMessage) => {
    if (error?.name === 'AbortError') return;
    if (typeof onError === 'function') {
      onError(error?.message || fallbackMessage);
    }
  }, [onError]);

  const openReadStatusDetail = useCallback(async (messageId) => {
    const normalizedMessageId = normalizeMessageId(messageId);
    if (!roomKey || !normalizedMessageId) return;
    setDetailOpen(true);
    setDetailMessageId(normalizedMessageId);
    setDetailLoading(true);
    try {
      const data = await fetchChatMessageReadStatusDetails({
        ...requestParams,
        messageId: normalizedMessageId,
      });
      setDetailReaders(Array.isArray(data?.readers) ? data.readers : []);
    } catch (error) {
      setDetailOpen(false);
      setDetailMessageId('');
      setDetailReaders([]);
      reportError(error, '既読一覧の取得に失敗しました。');
    } finally {
      setDetailLoading(false);
    }
  }, [reportError, requestParams, roomKey]);

  const closeReadStatusDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailMessageId('');
    setDetailReaders([]);
    setDetailLoading(false);
  }, []);

  useEffect(() => {
    closeReadStatusDetail();
  }, [closeReadStatusDetail, roomKey]);

  return {
    detailOpen,
    detailMessageId,
    detailReaders,
    detailLoading,
    openReadStatusDetail,
    closeReadStatusDetail,
  };
};

export default useChatMessageReadStatus;
